import THREE from "three";

/**
 * Unlit material for 3D point geometry.
 *
 * @alias Autodesk.Viewing.Scene.PointsMaterial
 * @memberOf Autodesk.Viewing.Scene
 * @class
 */
export class PointsMaterial extends THREE.PointCloudMaterial {
  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial
   * @param {object}                         [params]
   * @param {Autodesk.Viewing.Scene.Color}   [params.color]        Point color. Default: white.
   * @param {number}                         [params.opacity]      Point opacity in `[0, 1]`. Default: `1`.
   * @param {number}                         [params.size]         Point size in pixels. Default: `1`.
   * @param {Autodesk.Viewing.Scene.Texture} [params.map]          Texture map applied to each point sprite. Default: `null`.
   * @param {boolean}                        [params.vertexColors] Use per-vertex colors stored in the geometry. Default: `false`.
   * @param {boolean}                        [params.depthWrite]   Write to the depth buffer. Default: `true`.
   * @param {boolean}                        [params.depthTest]    Depth-test against the depth buffer. Default: `true`.
   * @param {boolean}                        [params.transparent]  Render in the transparent pass with alpha blending. Default: `false`.
   */
  constructor(params = {}) {
    super(params);
    // PointCloudMaterial does not support environment maps; prevent MaterialManager.addHDRMaterial
    // from assigning the scene reflection map, which would corrupt the rendered point color.
    this.disableEnvMap = true;
  }

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getColor
   * @returns {Autodesk.Viewing.Scene.Color} A copy of the point color.
   */
  getColor() {return this.color.clone();}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setColor
   * @param {Autodesk.Viewing.Scene.Color} c Point color. Copied into the existing Color.
   */
  setColor(c) {this.color.copy(c);this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getOpacity
   * @returns {number} Point opacity in `[0, 1]`.
   */
  getOpacity() {return this.opacity;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setOpacity
   * @param {number} o Point opacity in `[0, 1]`. Requires `setTransparent(true)` for `< 1`.
   */
  setOpacity(o) {this.opacity = o;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getSize
   * @returns {number} Point size in pixels.
   */
  getSize() {return this.size;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setSize
   * @param {number} s Point size in pixels.
   */
  setSize(s) {this.size = s;this.needsUpdate = true;}

  /**
   * Returns the texture map applied to each point sprite.
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getMap
   * @returns {?Autodesk.Viewing.Scene.Texture} Texture map, or `null`.
   */
  getMap() {return this.map;}

  /**
   * Sets the texture map applied to each point sprite.
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setMap
   * @param {?Autodesk.Viewing.Scene.Texture} t Texture map, or `null`.
   */
  setMap(t) {this.map = t;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getVertexColors
   * @returns {boolean} Whether to use per-vertex colors from the geometry's `color` attribute.
   */
  getVertexColors() {return this.vertexColors;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setVertexColors
   * @param {boolean} v Whether to use per-vertex colors.
   */
  setVertexColors(v) {this.vertexColors = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getDepthWrite
   * @returns {boolean} Whether to write to the depth buffer.
   */
  getDepthWrite() {return this.depthWrite;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setDepthWrite
   * @param {boolean} v Whether to write to the depth buffer.
   */
  setDepthWrite(v) {this.depthWrite = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getDepthTest
   * @returns {boolean} Whether to depth-test against the depth buffer.
   */
  getDepthTest() {return this.depthTest;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setDepthTest
   * @param {boolean} v Whether to depth-test against the depth buffer.
   */
  setDepthTest(v) {this.depthTest = v;this.needsUpdate = true;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#getTransparent
   * @returns {boolean} Whether the material renders in the transparent pass.
   */
  getTransparent() {return this.transparent;}

  /**
   * @alias Autodesk.Viewing.Scene.PointsMaterial#setTransparent
   * @param {boolean} v Whether the material renders in the transparent pass with alpha blending.
   */
  setTransparent(v) {this.transparent = v;this.needsUpdate = true;}
}